import { Component } from '@angular/core';
import {
  FormGroup,
  FormControl,
  Validators,
  ReactiveFormsModule,
  FormsModule,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { LucideAngularModule, ArrowUpDown, ChartColumn, IndianRupee } from 'lucide-angular';
import { Botmaa } from '../../shared/components/botmaa/botmaa';
import { FormField } from '../../shared/components/form-field/form-field';
import { PasswordInput } from '../../shared/components/password-input/password-input';
import { FormSubmitButton } from '../../shared/components/form-submit-button/form-submit-button';
import { AuthLink } from '../../shared/components/auth-link/auth-link';
import { AuthService, AuthResult } from '../../shared/services/auth/auth.service';
import { ToastService } from '../../shared/services/toast/toast.service';
import {
  fullNameValidator,
  FULL_NAME_VALIDATION_MESSAGE,
} from '../../shared/utils/full-name.validators';

@Component({
  selector: 'app-signup',
  standalone: true,
  templateUrl: './signup.html',
  styleUrls: ['./signup.scss'],
  imports: [
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    MatIconModule, // ✅ Register icons here
    LucideAngularModule,
    Botmaa,
    FormField,
    PasswordInput,
    FormSubmitButton,
    AuthLink,
  ],
  providers: [
    ...((
      LucideAngularModule.pick({
        ArrowUpDown,
        ChartColumn,
        IndianRupee,
      }) as { providers?: import('@angular/core').Provider[] }
    ).providers || []),
  ],
})
export class SignupComponent {
  signupForm: FormGroup;
  loading = false;
  showPassword = false;
  showConfirmPassword = false;
  error: string | null = null;

  constructor(
    private router: Router,
    private authService: AuthService,
    private toastService: ToastService,
  ) {
    this.signupForm = new FormGroup(
      {
        fullName: new FormControl('', [
          Validators.required,
          Validators.minLength(3),
          fullNameValidator(),
        ]),
        email: new FormControl('', [Validators.required, Validators.email]),
        password: new FormControl('', [Validators.required, Validators.minLength(6)]),
        confirmPassword: new FormControl('', [Validators.required]),
      },
      { validators: this.passwordMatchValidator },
    );
  }

  // Password Match Validator
  passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.get('password')?.value;
    const confirmPassword = control.get('confirmPassword')?.value;
    return password && confirmPassword && password !== confirmPassword ? { mismatch: true } : null;
  }

  // Getter for easy access in HTML
  get f() {
    return this.signupForm.controls;
  }

  // Validation getters for error display
  get fullNameInvalid(): boolean {
    const control = this.signupForm.get('fullName');
    return !!(control?.touched && control.invalid);
  }

  get emailInvalid(): boolean {
    const control = this.signupForm.get('email');
    return !!(control?.touched && control.invalid);
  }

  get passwordInvalid(): boolean {
    const control = this.signupForm.get('password');
    return !!(control?.touched && control.invalid);
  }

  get confirmPasswordInvalid(): boolean {
    const control = this.signupForm.get('confirmPassword');
    return !!(control?.touched && (control?.invalid || this.signupForm.errors?.['mismatch']));
  }

  get passwordMismatchError(): boolean {
    return !!(
      this.signupForm.errors?.['mismatch'] && this.signupForm.get('confirmPassword')?.touched
    );
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  handleSubmit(): void {
    // Mark all fields as touched to show validation errors
    this.signupForm.markAllAsTouched();

    // Check if form is invalid
    if (this.signupForm.invalid) {
      // Update validity for all controls to ensure errors are shown
      Object.keys(this.signupForm.controls).forEach((key) => {
        const control = this.signupForm.get(key);
        control?.updateValueAndValidity({ emitEvent: false });
      });

      // Show toaster notification for validation errors (align with API 422 messages)
      let errorMessage = 'Please fill in all required fields correctly.';
      const fullNameControl = this.signupForm.get('fullName');
      const emailControl = this.signupForm.get('email');
      const passwordControl = this.signupForm.get('password');
      const confirmPasswordControl = this.signupForm.get('confirmPassword');

      const emailEmpty = !!emailControl?.errors?.['required'];
      const passwordEmpty = !!passwordControl?.errors?.['required'];
      const fullNameEmpty = !!fullNameControl?.errors?.['required'];

      if (emailEmpty && passwordEmpty && fullNameEmpty) {
        errorMessage = 'Email,Password, fields should not be empty.';
      } else if (emailEmpty) {
        errorMessage = 'Email cannot be empty';
      } else if (fullNameEmpty) {
        errorMessage = 'Full name cannot be empty';
      } else if (passwordEmpty) {
        errorMessage = 'Password cannot be empty';
      } else if (fullNameControl?.invalid) {
        if (fullNameControl.errors?.['minlength']) {
          errorMessage = 'Full name must be at least 3 characters.';
        } else if (fullNameControl.errors?.['invalidFullName']) {
          errorMessage = FULL_NAME_VALIDATION_MESSAGE;
        }
      } else if (emailControl?.invalid) {
        if (emailControl.errors?.['email']) {
          errorMessage = 'Please enter a valid email address.';
        }
      } else if (passwordControl?.invalid) {
        if (passwordControl.errors?.['minlength']) {
          errorMessage = 'Password must be at least 6 characters.';
        }
      } else if (confirmPasswordControl?.invalid) {
        errorMessage = 'Please confirm your password.';
      } else if (this.signupForm.errors?.['mismatch']) {
        errorMessage = 'Passwords do not match.';
      }

      this.toastService.error(errorMessage, 'Validation Error');
      return;
    }

    this.loading = true;
    this.error = null;

    const { email, password, fullName } = this.signupForm.value;

    this.authService.register(email ?? '', password ?? '', fullName ?? undefined).subscribe({
      next: (result: AuthResult) => {
        this.loading = false;
        if (result.success) {
          this.router.navigate(['/login']);
        } else {
          this.error = result.error || 'Signup failed. Please try again.';
        }
      },
      error: (err) => {
        this.loading = false;
        this.error = err?.message || 'Unexpected error occurred.';
      },
    });
  }
}
